import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/integrations/supabase/client';

// Types
export interface Ouvidoria {
  id: string;
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string;
  naturezaJuridica: string;
  endereco: string;
  municipio: string;
  uf: string;
  telefone: string;
  email: string;
  website?: string;
  logo?: string;
  tipoOuvidoria: 'geral' | 'setorial';
  categorias: string[];
  status: 'ativo' | 'pendente' | 'suspenso';
  createdAt: string;
  updatedAt: string;
}

export interface OuvidoriaState {
  // Auth State
  isAuthenticated: boolean;
  currentOuvidoria: Ouvidoria | null;
  token: string | null;
  
  // Loading States
  isLoading: boolean;
  isValidatingCNPJ: boolean;
  isRegistering: boolean;
  isLoggingIn: boolean;
  
  // Error States
  error: string | null;
  cnpjError: string | null;
  loginError: string | null;
  
  // CNPJ Validation
  cnpjData: CNPJData | null;
  
  // Actions
  validateCNPJ: (cnpj: string) => Promise<boolean>;
  registerOuvidoria: (data: RegisterOuvidoriaData) => Promise<boolean>;
  login: (identifier: string, credential: string) => Promise<boolean>;
  logout: () => void;
  updateOuvidoria: (data: Partial<Ouvidoria>) => Promise<boolean>;
  clearErrors: () => void;
}

export interface CNPJData {
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string;
  endereco: string;
  numero: string;
  bairro: string;
  municipio: string;
  uf: string;
  cep: string;
  ibge: string;
  naturezaJuridica: string;
  atividadePrincipal: string;
  situacaoCadastral: string;
  isValid: boolean;
}

export interface RegisterOuvidoriaData {
  cnpj: string;
  email: string;
  telefone: string;
  senha: string;
  tipoOuvidoria: 'geral' | 'setorial';
  categorias: string[];
}

// Utility: Validate CNPJ format
const validateCNPJFormat = (cnpj: string): boolean => {
  const cleanCNPJ = cnpj.replace(/\D/g, '');
  
  if (cleanCNPJ.length !== 14) return false;
  if (/^(\d)\1+$/.test(cleanCNPJ)) return false;
  
  // Validate check digits
  let sum = 0;
  let weight = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleanCNPJ[i]) * weight[i];
  }
  
  let digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (parseInt(cleanCNPJ[12]) !== digit) return false;
  
  sum = 0;
  weight = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleanCNPJ[i]) * weight[i];
  }
  
  digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  return parseInt(cleanCNPJ[13]) === digit;
};

// Utility: Format CNPJ for display
export const formatCNPJ = (cnpj: string): string => {
  const clean = cnpj.replace(/\D/g, '');
  return clean.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

// Utility: Generate protocol number
export const generateProtocol = (): string => {
  const year = new Date().getFullYear();
  const sequence = Math.floor(Math.random() * 999999).toString().padStart(6, '0');
  return `${year}-${sequence}`;
};

// Validate CNPJ via Edge Function (bypasses CORS)
const validateCNPJViaEdgeFunction = async (cnpj: string): Promise<{
  valid: boolean;
  error?: string;
  data?: CNPJData;
}> => {
  const { data, error } = await supabase.functions.invoke('validate-cnpj', {
    body: { cnpj }
  });
  
  if (error) {
    throw new Error(error.message || 'Erro ao conectar com o servidor');
  }
  
  return data;
};

// Simulate API for non-ReceitaWS calls
const simulateAPI = async <T>(data: T, delay = 1000, shouldFail = false): Promise<T> => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (shouldFail) {
        reject(new Error('API Error'));
      } else {
        resolve(data);
      }
    }, delay);
  });
};

export const useOuvidoriaStore = create<OuvidoriaState>()(
  persist(
    (set, get) => ({
      // Initial State
      isAuthenticated: false,
      currentOuvidoria: null,
      token: null,
      isLoading: false,
      isValidatingCNPJ: false,
      isRegistering: false,
      isLoggingIn: false,
      error: null,
      cnpjError: null,
      loginError: null,
      cnpjData: null,

      // Validate CNPJ with Receita Federal via Edge Function
      validateCNPJ: async (cnpj: string) => {
        const cleanCNPJ = cnpj.replace(/\D/g, '');
        
        set({ isValidatingCNPJ: true, cnpjError: null, cnpjData: null });
        
        try {
          // Local format validation first
          if (!validateCNPJFormat(cleanCNPJ)) {
            set({ 
              isValidatingCNPJ: false, 
              cnpjError: 'CNPJ inválido. Verifique os dígitos.' 
            });
            return false;
          }
          
          // Call Edge Function to validate CNPJ
          const result = await validateCNPJViaEdgeFunction(cleanCNPJ);
          
          if (!result.valid) {
            set({ 
              isValidatingCNPJ: false, 
              cnpjError: result.error || 'CNPJ inválido',
              cnpjData: null
            });
            return false;
          }
          
          // Build CNPJData from response
          const cnpjData: CNPJData = {
            ...result.data!,
            isValid: true,
          };
          
          set({ 
            isValidatingCNPJ: false, 
            cnpjData,
            cnpjError: null 
          });
          return true;
          
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Erro ao consultar CNPJ';
          set({ 
            isValidatingCNPJ: false, 
            cnpjError: errorMessage,
            cnpjData: null
          });
          return false;
        }
      },

      // Register new Ouvidoria
      registerOuvidoria: async (data: RegisterOuvidoriaData) => {
        const { cnpjData } = get();
        
        if (!cnpjData) {
          set({ error: 'CNPJ não validado. Valide o CNPJ primeiro.' });
          return false;
        }
        
        if (data.categorias.length === 0) {
          set({ error: 'Selecione pelo menos uma categoria de atuação.' });
          return false;
        }
        
        set({ isRegistering: true, error: null });
        
        try {
          // Simulate API call
          // In production: POST ${API_BASE}/ouvidorias/register
          const newOuvidoria = await simulateAPI<Ouvidoria>({
            id: crypto.randomUUID(),
            cnpj: cnpjData.cnpj,
            razaoSocial: cnpjData.razaoSocial,
            nomeFantasia: cnpjData.nomeFantasia,
            naturezaJuridica: cnpjData.naturezaJuridica,
            endereco: cnpjData.endereco,
            municipio: cnpjData.municipio,
            uf: cnpjData.uf,
            telefone: data.telefone,
            email: data.email,
            tipoOuvidoria: data.tipoOuvidoria,
            categorias: data.categorias,
            status: 'ativo',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }, 2000);
          
          const token = `urbnet_${crypto.randomUUID()}`;
          
          set({ 
            isRegistering: false, 
            isAuthenticated: true,
            currentOuvidoria: newOuvidoria,
            token,
            error: null
          });
          
          return true;
          
        } catch (error) {
          set({ 
            isRegistering: false, 
            error: 'Erro ao cadastrar ouvidoria. Tente novamente.' 
          });
          return false;
        }
      },

      // Login
      login: async (identifier: string, credential: string) => {
        set({ isLoggingIn: true, loginError: null });
        
        try {
          // Validate inputs
          if (!identifier || !credential) {
            set({ 
              isLoggingIn: false, 
              loginError: 'Preencha todos os campos.' 
            });
            return false;
          }
          
          // Simulate API call
          // In production: POST ${API_BASE}/ouvidorias/login
          const response = await simulateAPI<{ ouvidoria: Ouvidoria; token: string }>({
            ouvidoria: {
              id: crypto.randomUUID(),
              cnpj: '46395000000139',
              razaoSocial: 'Prefeitura Municipal de São Paulo',
              nomeFantasia: 'Ouvidoria SP',
              naturezaJuridica: 'Autarquia Municipal',
              endereco: 'Viaduto do Chá, 15 - Centro Histórico',
              municipio: 'São Paulo',
              uf: 'SP',
              telefone: '(11) 3113-8000',
              email: 'ouvidoria@prefeitura.sp.gov.br',
              website: 'https://sp156.prefeitura.sp.gov.br',
              tipoOuvidoria: 'geral',
              categorias: ['infraestrutura', 'iluminacao', 'limpeza', 'saneamento', 'transito'],
              status: 'ativo',
              createdAt: '2024-01-01T00:00:00Z',
              updatedAt: new Date().toISOString(),
            },
            token: `urbnet_${crypto.randomUUID()}`,
          }, 1500);
          
          set({ 
            isLoggingIn: false, 
            isAuthenticated: true,
            currentOuvidoria: response.ouvidoria,
            token: response.token,
            loginError: null
          });
          
          return true;
          
        } catch (error) {
          set({ 
            isLoggingIn: false, 
            loginError: 'Credenciais inválidas ou erro de conexão.' 
          });
          return false;
        }
      },

      // Logout
      logout: () => {
        set({
          isAuthenticated: false,
          currentOuvidoria: null,
          token: null,
          cnpjData: null,
          error: null,
          loginError: null,
          cnpjError: null,
        });
      },

      // Update Ouvidoria
      updateOuvidoria: async (data: Partial<Ouvidoria>) => {
        const { currentOuvidoria, token } = get();
        
        if (!currentOuvidoria || !token) {
          set({ error: 'Sessão expirada. Faça login novamente.' });
          return false;
        }
        
        set({ isLoading: true, error: null });
        
        try {
          // Simulate API call
          // In production: PATCH ${API_BASE}/ouvidorias/${currentOuvidoria.id}
          const updated = await simulateAPI<Ouvidoria>({
            ...currentOuvidoria,
            ...data,
            updatedAt: new Date().toISOString(),
          }, 1000);
          
          set({ 
            isLoading: false, 
            currentOuvidoria: updated,
            error: null 
          });
          
          return true;
          
        } catch (error) {
          set({ 
            isLoading: false, 
            error: 'Erro ao atualizar dados. Tente novamente.' 
          });
          return false;
        }
      },

      // Clear all errors
      clearErrors: () => {
        set({ 
          error: null, 
          cnpjError: null, 
          loginError: null 
        });
      },
    }),
    {
      name: 'urbnet-ouvidoria-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        currentOuvidoria: state.currentOuvidoria,
        token: state.token,
      }),
    }
  )
);
